import { ProgressSpinner } from "primereact/progressspinner";
import { useSelector } from "react-redux";
import { useRef, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { chatActions } from "../../../di/di";
import styles from "../style/chat_body.module.css";
import { ChatUIAction, getPreviousMessages, ChatPrevMsgStatus } from "../state/chat_slice";
import ChatItemComp from "./chat_item_comp";
import { ArrowDown, Heart } from "lucide-react";
import { ChatMap } from "./chat_item_comp";
const imgEve = "/images/eve_profile.svg";

const ChatBodyComponent = () => {
  const scrollContainerRef = useRef(null);
  const dispatch = useDispatch();

  const messageList = useSelector((state) => state.chat.messageList);
  const isScrollToBottom = useSelector((state) => state.chat.isScrollToBottom);
  const isTyping = useSelector((state) => state.chat.isTyping);
  const isGeneratingImage = useSelector((state) => state.chat.isGeneratingImage);
  const performFocusToIndex = useSelector((state) => state.chat.performFocusToIndex);
  const focusIndex = useSelector((state) => state.chat.focusIndex);
  const chatPrevMsgStatus = useSelector((state) => state.chat.chatPrevMsgStatus);
  const avatarProfile = useSelector((state) => state.modelProfile.profile);
  const [allowRangeChecking, setAllowRangeChecking] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const scrollRef = useRef(null);

  const scrollToBottom = (behavior = "smooth") => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: behavior,
      });
    }
  };

  const scrollToIndex = (index, behavior = "auto") => {
    if (scrollContainerRef.current && messageList.length > index) {
      console.log("PERFORM SCROLL TO INDEX", index);
      const messageElements = scrollContainerRef.current.querySelectorAll("[data-message-index]");
      if (messageElements && messageElements.length > index) {
        messageElements[index].scrollIntoView({ behavior });
      }
      setAllowRangeChecking(true);
    }
  };

  // Scroll to bottom when user is typing
  useEffect(() => {
    if (isTyping || isGeneratingImage) {
      setTimeout(scrollToBottom, 200); // Small delay to ensure the UI updates before scrolling
    }
  }, [isTyping, isGeneratingImage]);

  // Ensure it scrolls when a UI action is triggered
  useEffect(() => {
    if (isScrollToBottom) {
      setTimeout(scrollToBottom, 200);
      dispatch(chatActions.setIsScrollToBottom(false));
    }
  }, [isScrollToBottom]);

  useEffect(() => {
    if (performFocusToIndex) {
      console.log("Perform Focus to index");
      setTimeout(() => scrollToIndex(focusIndex), 200);
      dispatch(chatActions.setPerformFocusToIndex(false));
    }
  }, [performFocusToIndex]);

  // Check if we're at the top of the scroll container to load previous messages
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

      // Check if at top - load previous messages
      if (scrollTop < 50 && allowRangeChecking) {
        setAllowRangeChecking(false);
        retryGetPrevMessages();
      }

      // Check if at bottom
      const atBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 150;
      setIsAtBottom(atBottom);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [allowRangeChecking, chatPrevMsgStatus]);

  // Initial scroll to bottom
  useEffect(() => {
    if (messageList.length > 0) {
      scrollToBottom("auto");
    }
  }, [dispatch]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (isAtBottom && messageList.length > 0) {
      scrollToBottom();
    }
  }, [messageList.length]);

  const retryGetPrevMessages = () => {
    if (
      chatPrevMsgStatus === ChatPrevMsgStatus.topReached ||
      chatPrevMsgStatus === ChatPrevMsgStatus.getOngoing ||
      chatPrevMsgStatus === ChatPrevMsgStatus.none
    ) {
      return;
    }
    dispatch(getPreviousMessages());
  };

  const HeaderComponent = () => (
    <>
      {chatPrevMsgStatus === ChatPrevMsgStatus.getOngoing && (
        <div className="w-full flex flex-col flex items-center justify-center">
          <div className="h-7" />
          <ProgressSpinner strokeWidth="3" animationDuration=".5s" className="chat-prev-msg-loading-spinner" />
          <div className="h-7" />
        </div>
      )}
      {chatPrevMsgStatus === ChatPrevMsgStatus.getFailed && (
        <div className="w-full flex flex-col items-center justify-center">
          <div className="h-7"></div>
          <div className="text-sm">Failed to load previous messages</div>
          <div className="h-2"></div>
          <button
            className="bg-slate-800/40 text-pink-300 px-4 py-1 rounded-full backdrop-blur-sm shadow-sm text-xs hover:bg-slate-700/50 transition-colors"
            onClick={retryGetPrevMessages}
          >
            Retry
          </button>
          <div className="h-7"></div>
        </div>
      )}
      {chatPrevMsgStatus === ChatPrevMsgStatus.topReached && (
        <div className="w-full flex flex-col items-center justify-center">
          <div className="h-7"></div>
          <div className="text-sm">You've reached the beginning of the conversation</div>
          <div className="h-7"></div>
        </div>
      )}
    </>
  );

  const FooterComponent = () => (
    <div className="flex flex-col relative max-w-5xl mx-auto">
      {isTyping && (
        <div className={`flex flex-col max-w-[80%]`}>
          <div className="h-2" />
          <div className={`flex flex-row items-start pt-5 space-x-3 px-5 ${styles.messageWrapperParent}`}>
            <div
              className={`relative w-8 h-8 rounded-full bg-gradient-to-r from-red-700 to-red-800 flex items-center justify-center shadow-md shrink-0`}
            >
              <Heart size={16} className="absolute text-pink-100" />
              <div className="absolute flex items-center justify-center">
                <div className="rounded-full w-7 h-7 overflow-hidden flex items-center justify-center">
                  {(avatarProfile?.profileUrl ?? "") !== "" && (
                    <div
                      className="rounded-full w-7 h-7 overflow-hidden flex items-center justify-center"
                      style={{ background: `url('${avatarProfile?.profileUrl}') top / cover`, backgroundRepeat: "no-repeat" }}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-col text-base bg-white/10 text-white rounded-2xl rounded-tl-sm px-4 backdrop-blur-sm border border-red-900/10 shadow-md">
                <div className="h-4" />
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 bg-pink-300 rounded-full ${styles.animateTypingBubble}`} style={{ animationDelay: "0s" }}></div>
                  <div className={`w-2 h-2 bg-pink-300 rounded-full ${styles.animateTypingBubble}`} style={{ animationDelay: "0.45s" }}></div>
                  <div className={`w-2 h-2 bg-pink-300 rounded-full ${styles.animateTypingBubble}`} style={{ animationDelay: "0.6s" }}></div>
                </div>{" "}
                <div className="h-4" />
              </div>
            </div>
          </div>
          <div className="h-5" />
        </div>
      )}

      {isGeneratingImage && (
        <div className={`flex flex-col max-w-[80%]`}>
          <div className="h-2" />
          <div className={`flex flex-row items-start pt-5 space-x-3 px-5 ${styles.messageWrapperParent}`}>
            <div
              className={`relative w-8 h-8 rounded-full bg-gradient-to-r from-red-700 to-red-800 flex items-center justify-center shadow-md shrink-0`}
            >
              <Heart size={16} className="absolute text-pink-100" />
              <div className="absolute flex items-center justify-center">
                <div className="rounded-full w-7 h-7 overflow-hidden flex items-center justify-center">
                  {(avatarProfile?.profileUrl ?? "") !== "" && (
                    <div
                      className="rounded-full w-7 h-7 overflow-hidden flex items-center justify-center"
                      style={{ background: `url('${avatarProfile?.profileUrl}') top / cover`, backgroundRepeat: "no-repeat" }}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-col text-base bg-white/10 text-white rounded-2xl rounded-tl-sm px-4 backdrop-blur-sm border border-red-900/10 shadow-md">
                <div className="h-3" />
                <div className="flex flex-row items-center space-x-2">
                  <svg className="animate-spin h-4 w-4 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span className="ml-2 text-white text-base">Sending photo...</span>
                </div>
                <div className="h-3" />
              </div>
            </div>
          </div>
          <div className="h-5" />
        </div>
      )}

      {!isTyping && !isGeneratingImage && <div className="h-5" />}
    </div>
  );

  return (
    <div
      ref={scrollRef}
      className="flex-1 flex flex-col relative overflow-auto bg-transparent w-full custom-scrollbar"
      style={{
        backgroundImage: "radial-gradient(circle at center, rgba(185, 28, 28, 0.03) 0%, transparent 70%)",
      }}
    >
      <div className="relative flex-1 h-full w-full">
        <div ref={scrollContainerRef} className="absolute inset-0 overflow-y-auto custom-scrollbar">
          <HeaderComponent />
          <ChatMap messageList={messageList} />
          <FooterComponent />
        </div>
      </div>
      {!isAtBottom && (
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 pb-3 z-[30]">
          <button
            onClick={() => scrollToBottom()}
            className="bg-red-500/40 backdrop-blur-sm hover:opacity-90 text-white p-2 sm:p-2 rounded-full transition-colors border border-white/10"
          >
            <ArrowDown size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatBodyComponent;
